/** @jsxImportSource @emotion/react */
import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { InputNumber, Slider, Space, Typography, Divider, Tag, Segmented, Drawer, Select } from 'antd';
import { css } from '@emotion/react';
import type { DailyWeatherPoint } from '../../utils/climatePresets';
import { applyCloudCoverToPoint, estimateAstronomicalDaylightHours } from './weatherMapUtils';

const { Text } = Typography;

// ---------- constants ----------

const MONTH_NAMES = [
  'Янв', 'Фев', 'Мар', 'Апр', 'Май', 'Июн',
  'Июл', 'Авг', 'Сен', 'Окт', 'Ноя', 'Дек',
];
const DAYS_IN_MONTH = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

// ---------- styles ----------

const styles = {
  wrapper: css({
    position: 'relative',
    userSelect: 'none',
    overflowX: 'auto',
  }),
  grid: css({
    display: 'grid',
    gridTemplateColumns: '48px repeat(31, 1fr)',
    gap: 1,
    minWidth: 800,
  }),
  monthLabel: css({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 600,
    fontSize: 11,
    padding: '2px 0',
    background: '#fafafa',
    borderRadius: 2,
    minHeight: 32,
  }),
  dayHeader: css({
    textAlign: 'center',
    fontSize: 10,
    color: '#999',
    padding: 2,
  }),
  cell: css({
    position: 'relative',
    minWidth: 22,
    minHeight: 32,
    borderRadius: 3,
    cursor: 'pointer',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 9,
    lineHeight: 1.2,
    transition: 'box-shadow 0.15s, transform 0.1s',
    border: '1px solid transparent',
    '&:hover': {
      transform: 'scale(1.15)',
      zIndex: 2,
      boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
    },
  }),
  cellSelected: css({
    border: '2px solid #1677ff !important',
    boxShadow: '0 0 0 1px #1677ff',
    zIndex: 1,
  }),
  cellEmpty: css({
    background: '#f5f5f5',
    cursor: 'cell',
    color: '#bbb',
    border: '1px dashed #d9d9d9',
    '&:hover': { background: '#eef3ff', borderColor: '#69b1ff', transform: 'scale(1.1)', boxShadow: 'none' },
  }),
  // A forecast day (near future) — shown faintly vs observed history.
  cellForecast: css({
    opacity: 0.5,
    backgroundImage: 'repeating-linear-gradient(45deg, rgba(255,255,255,0.35) 0 3px, transparent 3px 6px)',
  }),
  // The current day (observed/forecast boundary) — bright frame.
  cellToday: css({
    border: '2px solid #fa8c16 !important',
    boxShadow: '0 0 0 2px rgba(250,140,22,0.45), 0 0 6px rgba(250,140,22,0.6)',
    zIndex: 3,
  }),
  cellEdited: css({
    '&::after': {
      content: '""', position: 'absolute', top: 1, right: 1,
      width: 4, height: 4, borderRadius: '50%', background: '#faad14',
    },
  }),
  sourceManual: css({
    '&::before': {
      content: '""', position: 'absolute', top: 1, left: 1,
      width: 4, height: 4, borderRadius: '50%', background: '#fa8c16',
    },
  }),
  sourceApi: css({
    '&::before': {
      content: '""', position: 'absolute', top: 1, left: 1,
      width: 4, height: 4, borderRadius: '50%', background: '#1677ff',
    },
  }),
  precipBar: css({
    width: '80%', height: 3, borderRadius: 1, background: '#69b1ff', marginTop: 1,
  }),
  toolbar: css({
    display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 8,
  }),
  legend: css({
    display: 'flex', gap: 4, alignItems: 'center', fontSize: 11, marginLeft: 'auto',
  }),
  legendItem: css({
    width: 12, height: 12, borderRadius: 2, display: 'inline-block',
  }),
  applyBtn: css({
    width: '100%', textAlign: 'center', cursor: 'pointer', color: '#fff',
    background: '#1677ff', borderRadius: 6, padding: '6px 0', fontSize: 13,
    fontWeight: 500, marginTop: 12, '&:hover': { background: '#4096ff' },
  }),
};

// ---------- temperature → color ----------

function tempToColor(tempAvg: number): string {
  if (tempAvg <= -20) return '#1e3a5f';
  if (tempAvg <= -10) return '#4a7fb5';
  if (tempAvg <= 0) return '#91c7e8';
  if (tempAvg <= 5) return '#c6e3f7';
  if (tempAvg <= 10) return '#e8f0d0';
  if (tempAvg <= 15) return '#fff9c4';
  if (tempAvg <= 20) return '#ffe082';
  if (tempAvg <= 25) return '#ffb74d';
  if (tempAvg <= 30) return '#ef6c00';
  return '#c62828';
}

// ---------- helpers ----------

function monthDayToDoy(month: number, day: number): number {
  let doy = 0;
  for (let m = 0; m < month; m++) doy += DAYS_IN_MONTH[m];
  return doy + day;
}

function doyToMMDD(doy: number): string {
  let remaining = doy;
  for (let m = 0; m < 12; m++) {
    if (remaining <= DAYS_IN_MONTH[m]) {
      return `${String(m + 1).padStart(2, '0')}-${String(remaining).padStart(2, '0')}`;
    }
    remaining -= DAYS_IN_MONTH[m];
  }
  return '12-31';
}

// ---------- interfaces ----------

export interface WeatherCalendarEditorProps {
  points: DailyWeatherPoint[];
  onChange: (points: DailyWeatherPoint[]) => void;
  readOnly?: boolean;
}

interface AdjustValues {
  tempMinBiasC: number;
  tempMaxBiasC: number;
  precipMul: number;
  cloudBias: number;
}

interface ExactValues {
  tempMin: number | null;
  tempMax: number | null;
  precipitationMm: number | null;
  humidityPct: number | null;
  cloudCover: number | null;
}

type EditMode = 'exact' | 'relative';

const INITIAL_ADJUST: AdjustValues = { tempMinBiasC: 0, tempMaxBiasC: 0, precipMul: 1, cloudBias: 0 };
const INITIAL_EXACT: ExactValues = { tempMin: null, tempMax: null, precipitationMm: null, humidityPct: null, cloudCover: null };

// ---------- component ----------

const WeatherCalendarEditor: React.FC<WeatherCalendarEditorProps> = ({ points, onChange, readOnly }) => {
  const [selectedCells, setSelectedCells] = useState<Set<number>>(new Set());
  const [editedCells, setEditedCells] = useState<Set<number>>(new Set());
  const [adjustValues, setAdjustValues] = useState<AdjustValues>(INITIAL_ADJUST);
  const [exactValues, setExactValues] = useState<ExactValues>(INITIAL_EXACT);
  const [editMode, setEditMode] = useState<EditMode>('relative');
  const dragStartRef = useRef<number | null>(null);
  const isDraggingRef = useRef(false);
  const justDraggedRef = useRef(false);

  // Build a lookup: doy → point
  const pointMap = useMemo(() => {
    const map = new Map<number, DailyWeatherPoint>();
    points.forEach(p => map.set(p.dayOfYear, p));
    return map;
  }, [points]);

  // Real-year provenance: "today" is the last observed day (observed/forecast
  // boundary), shown only when a forecast tail exists. Forecast days render faintly;
  // days past the forecast are simply absent (empty, hand-fillable).
  const todayDoy = useMemo(() => {
    let lastObserved: number | null = null;
    let hasForecast = false;
    for (const p of points) {
      if (p.dataKind === 'forecast') hasForecast = true;
      else if (p.dataKind === 'observed') lastObserved = Math.max(lastObserved ?? 0, p.dayOfYear);
    }
    return hasForecast ? lastObserved : null;
  }, [points]);

  // Grid: 12 months × 31 days
  const grid = useMemo(() => {
    const rows: { month: number; cells: (DailyWeatherPoint | null)[] }[] = [];
    let doy = 1;
    for (let m = 0; m < 12; m++) {
      const cells: (DailyWeatherPoint | null)[] = [];
      for (let d = 1; d <= 31; d++) {
        if (d <= DAYS_IN_MONTH[m]) {
          cells.push(pointMap.get(doy) ?? null);
          doy++;
        } else {
          cells.push(null);
        }
      }
      rows.push({ month: m, cells });
    }
    return rows;
  }, [pointMap]);

  // ─── Selection handlers ───────────────────────────────

  const handleMouseDown = useCallback((doy: number, e: React.MouseEvent) => {
    if (readOnly) return;
    e.preventDefault();
    isDraggingRef.current = true;
    justDraggedRef.current = false;
    dragStartRef.current = doy;

    if (e.shiftKey && selectedCells.size > 0) {
      const existing = Array.from(selectedCells);
      const min = Math.min(...existing, doy);
      const max = Math.max(...existing, doy);
      const range = new Set<number>();
      for (let i = min; i <= max; i++) range.add(i);
      setSelectedCells(range);
    } else if (e.ctrlKey || e.metaKey) {
      setSelectedCells(prev => {
        const next = new Set(prev);
        if (next.has(doy)) next.delete(doy); else next.add(doy);
        return next;
      });
    } else {
      setSelectedCells(new Set([doy]));
    }
  }, [readOnly, selectedCells]);

  const handleMouseEnter = useCallback((doy: number) => {
    if (!isDraggingRef.current || dragStartRef.current === null) return;
    justDraggedRef.current = true;
    const start = dragStartRef.current;
    const min = Math.min(start, doy);
    const max = Math.max(start, doy);
    const range = new Set<number>();
    for (let i = min; i <= max; i++) range.add(i);
    setSelectedCells(range);
  }, []);

  const handleMouseUp = useCallback(() => {
    isDraggingRef.current = false;
  }, []);

  const handleGridBackgroundClick = useCallback((e: React.MouseEvent) => {
    if (justDraggedRef.current) { justDraggedRef.current = false; return; }
    if (e.target === e.currentTarget) setSelectedCells(new Set());
  }, []);

  const handleSelectMonth = useCallback((monthIdx: number) => {
    if (readOnly) return;
    const start = monthDayToDoy(monthIdx, 1);
    const end = start + DAYS_IN_MONTH[monthIdx] - 1;
    const range = new Set<number>();
    for (let i = start; i <= end; i++) range.add(i);
    setSelectedCells(range);
  }, [readOnly]);

  // Reset overlay state when selection count changes to multi
  useEffect(() => {
    if (selectedCells.size > 1) {
      setAdjustValues(INITIAL_ADJUST);
      setExactValues(INITIAL_EXACT);
      setEditMode('relative');
    }
  }, [selectedCells.size]);

  // Keep the editor open while values are edited; only prune days that no
  // longer exist in the incoming calendar data.
  useEffect(() => {
    const validDays = new Set(points.map(p => p.dayOfYear));
    const pruneInvalidDays = (previous: Set<number>) => {
      const next = new Set<number>();
      let changed = false;
      previous.forEach((doy) => {
        if (validDays.has(doy)) {
          next.add(doy);
        } else {
          changed = true;
        }
      });
      return changed ? next : previous;
    };

    setSelectedCells(pruneInvalidDays);
    setEditedCells(pruneInvalidDays);
  }, [points]);

  // ─── Edit handlers ────────────────────────────────────

  // Fill an empty (e.g. past-forecast) day by hand: seed it from the nearest known
  // day, mark it manual, and open it for editing. Lets the player author a custom
  // weather "техкарта" beyond the forecast horizon.
  const handleFillEmpty = useCallback((doy: number) => {
    if (readOnly) return;
    let nearest: DailyWeatherPoint | null = null;
    let bestDist = Infinity;
    for (const p of points) {
      const dist = Math.abs(p.dayOfYear - doy);
      if (dist < bestDist) { bestDist = dist; nearest = p; }
    }
    const template: DailyWeatherPoint = nearest ?? {
      dayOfYear: doy, tempMin: 5, tempMax: 15, precipitationMm: 0, humidityPct: 60,
      astronomicalDaylightHours: estimateAstronomicalDaylightHours(doy), cloudCover: 0.4, outdoorSunHours: 6, solarPpfdUmol: 500,
    };
    const newPoint: DailyWeatherPoint = {
      ...template,
      dayOfYear: doy,
      date: doyToMMDD(doy),
      source: 'manual',
      dataKind: undefined,
      cloudEdited: true,
    };
    onChange([...points, newPoint].sort((a, b) => a.dayOfYear - b.dayOfYear));
    setSelectedCells(new Set([doy]));
    setEditedCells(prev => new Set(prev).add(doy));
  }, [points, onChange, readOnly]);

  const handleSingleEdit = useCallback((doy: number, field: keyof DailyWeatherPoint, value: number | null) => {
    if (readOnly) return;
    const updated = points.map(p => {
      if (p.dayOfYear !== doy) return p;
      if (field === 'cloudCover' && value != null) {
        return { ...applyCloudCoverToPoint(p, value), source: 'manual' as const };
      }
      return { ...p, [field]: value, source: 'manual' as const };
    });
    setEditedCells(prev => new Set(prev).add(doy));
    onChange(updated);
  }, [points, onChange, readOnly]);

  const handleApplyRelative = useCallback(() => {
    if (selectedCells.size === 0) return;
    const updated = points.map(p => {
      if (!selectedCells.has(p.dayOfYear)) return p;
      const nextCloudCover = Math.max(0, Math.min(1, +(p.cloudCover + adjustValues.cloudBias / 100).toFixed(2)));
      return {
        ...applyCloudCoverToPoint(p, nextCloudCover),
        tempMin: +(p.tempMin + adjustValues.tempMinBiasC).toFixed(1),
        tempMax: +(p.tempMax + adjustValues.tempMaxBiasC).toFixed(1),
        precipitationMm: Math.max(0, +(p.precipitationMm * adjustValues.precipMul).toFixed(1)),
        source: 'manual' as const,
      };
    });
    setEditedCells(prev => {
      const next = new Set(prev);
      selectedCells.forEach(doy => next.add(doy));
      return next;
    });
    onChange(updated);
    setAdjustValues(INITIAL_ADJUST);
  }, [points, selectedCells, adjustValues, onChange]);

  const handleApplyExact = useCallback(() => {
    if (selectedCells.size === 0) return;
    const updated = points.map(p => {
      if (!selectedCells.has(p.dayOfYear)) return p;
      let patched: DailyWeatherPoint = { ...p, source: 'manual' as const };
      if (exactValues.tempMin != null) patched.tempMin = exactValues.tempMin;
      if (exactValues.tempMax != null) patched.tempMax = exactValues.tempMax;
      if (exactValues.precipitationMm != null) patched.precipitationMm = exactValues.precipitationMm;
      if (exactValues.humidityPct != null) patched.humidityPct = exactValues.humidityPct;
      if (exactValues.cloudCover != null) patched = applyCloudCoverToPoint(patched, exactValues.cloudCover);
      return patched;
    });
    setEditedCells(prev => {
      const next = new Set(prev);
      selectedCells.forEach(doy => next.add(doy));
      return next;
    });
    onChange(updated);
    setExactValues(INITIAL_EXACT);
  }, [points, selectedCells, exactValues, onChange]);

  // ─── Derived values for overlay ───────────────────────

  const selectedArray = useMemo(() => Array.from(selectedCells).sort((a, b) => a - b), [selectedCells]);
  const singlePoint = selectedArray.length === 1 ? pointMap.get(selectedArray[0]) ?? null : null;
  const isMulti = selectedArray.length > 1;
  const showOverlay = selectedArray.length > 0 && !readOnly;

  // ─── Render ───────────────────────────────────────────

  return (
    <div css={styles.wrapper} onMouseUp={handleMouseUp} onMouseLeave={handleMouseUp}>
        {/* Toolbar */}
        <div css={styles.toolbar}>
          {selectedCells.size > 0 && <Tag color="blue">{selectedCells.size} дн. выбрано</Tag>}
          {editedCells.size > 0 && <Tag color="gold">{editedCells.size} изменено</Tag>}
          {todayDoy != null && (
            <Space size={4} style={{ fontSize: 10 }}>
              <span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 2, border: '2px solid #fa8c16' }} />
              <Text type="secondary" style={{ fontSize: 10 }}>сегодня</Text>
              <span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 2, background: '#ffb74d', opacity: 0.5 }} />
              <Text type="secondary" style={{ fontSize: 10 }}>прогноз</Text>
              <span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 2, background: '#f5f5f5', border: '1px dashed #d9d9d9' }} />
              <Text type="secondary" style={{ fontSize: 10 }}>пусто (заполнить)</Text>
            </Space>
          )}
          <div css={styles.legend}>
            <span css={styles.legendItem} style={{ background: tempToColor(-15) }} />
            <Text type="secondary" style={{ fontSize: 10 }}>−15</Text>
            <span css={styles.legendItem} style={{ background: tempToColor(0) }} />
            <Text type="secondary" style={{ fontSize: 10 }}>0</Text>
            <span css={styles.legendItem} style={{ background: tempToColor(15) }} />
            <Text type="secondary" style={{ fontSize: 10 }}>15</Text>
            <span css={styles.legendItem} style={{ background: tempToColor(30) }} />
            <Text type="secondary" style={{ fontSize: 10 }}>30°C</Text>
          </div>
        </div>

        {/* Grid */}
        <div css={styles.grid} onClick={handleGridBackgroundClick}>
          <div />
          {Array.from({ length: 31 }, (_, i) => (
            <div key={`dh-${i}`} css={styles.dayHeader}>{i + 1}</div>
          ))}

          {grid.map(({ month, cells }) => (
            <React.Fragment key={`m-${month}`}>
              <div
                css={styles.monthLabel}
                onClick={() => handleSelectMonth(month)}
                title={`Выбрать весь ${MONTH_NAMES[month]}`}
                style={{ cursor: readOnly ? 'default' : 'pointer' }}
              >
                {MONTH_NAMES[month]}
              </div>
              {cells.map((point, dayIdx) => {
                if (!point) {
                  const isValidDay = dayIdx < DAYS_IN_MONTH[month];
                  if (!isValidDay) return <div key={`x-${month}-${dayIdx}`} style={{ visibility: 'hidden' }} />;
                  const emptyDoy = monthDayToDoy(month, dayIdx + 1);
                  return (
                    <div
                      key={`e-${month}-${dayIdx}`}
                      css={[styles.cell, styles.cellEmpty]}
                      title={readOnly ? 'Нет данных' : 'Заполнить вручную'}
                      onMouseDown={() => !readOnly && handleFillEmpty(emptyDoy)}
                    >
                      {!readOnly && <span style={{ fontSize: 12 }}>+</span>}
                    </div>
                  );
                }
                const doy = point.dayOfYear;
                const tempAvg = (point.tempMin + point.tempMax) / 2;
                const isSelected = selectedCells.has(doy);
                const isEdited = editedCells.has(doy);
                const isToday = doy === todayDoy;
                // A forecast day the player has hand-edited carries manual confidence,
                // so it's no longer a faint "прогноз" — render it as authored weather.
                const isForecast = point.dataKind === 'forecast' && point.source !== 'manual';
                const precipRatio = Math.min(1, point.precipitationMm / 8);
                return (
                  <div
                    key={`c-${doy}`}
                    css={[
                      styles.cell,
                      isForecast && styles.cellForecast,
                      isSelected && styles.cellSelected,
                      isToday && styles.cellToday,
                      isEdited && styles.cellEdited,
                      point.source === 'manual' && styles.sourceManual,
                      point.source === 'weather_api' && styles.sourceApi,
                    ]}
                    style={{ background: tempToColor(tempAvg) }}
                    title={isToday ? 'Текущий день' : isForecast ? 'Прогноз' : undefined}
                    onMouseDown={(e) => handleMouseDown(doy, e)}
                    onMouseEnter={() => handleMouseEnter(doy)}
                  >
                    <span style={{ fontWeight: 600 }}>{Math.round(tempAvg)}°</span>
                    {[95, 96, 99].includes(point.thunderstormCode ?? point.weatherCode ?? -1) && <span title="Гроза"> ⚡</span>}
                    {precipRatio > 0.05 && (
                      <div css={styles.precipBar} style={{ width: `${precipRatio * 80}%` }} />
                    )}
                  </div>
                );
              })}
            </React.Fragment>
          ))}
        </div>

      {/* ─── Drawer for editing ─── */}
      <Drawer
        open={showOverlay && singlePoint != null}
        onClose={() => setSelectedCells(new Set())}
        title={singlePoint ? `День ${singlePoint.dayOfYear} (${singlePoint.date})` : ''}
        width={360}
        mask={false}
        destroyOnClose
      >
        {singlePoint && (() => {
          const doy = singlePoint.dayOfYear;
          const srcLabel = singlePoint.source === 'manual' ? 'ручной ввод'
            : singlePoint.source === 'weather_api' ? 'внешний API' : 'климанорма';
          return (
            <>
              <Tag style={{ marginBottom: 8 }}>{srcLabel}</Tag>
              <Divider style={{ margin: '6px 0' }} />
              <Space direction="vertical" size={8} style={{ width: '100%' }}>
                <Space>
                  <Text style={{ width: 90 }}>T ночь:</Text>
                  <InputNumber size="small" value={singlePoint.tempMin} step={0.5}
                    onChange={v => v != null && handleSingleEdit(doy, 'tempMin', v)} style={{ width: 100 }} />
                  <Text type="secondary">°C</Text>
                </Space>
                <Space>
                  <Text style={{ width: 90 }}>T день:</Text>
                  <InputNumber size="small" value={singlePoint.tempMax} step={0.5}
                    onChange={v => v != null && handleSingleEdit(doy, 'tempMax', v)} style={{ width: 100 }} />
                  <Text type="secondary">°C</Text>
                </Space>
                <Space>
                  <Text style={{ width: 90 }}>Осадки:</Text>
                  <InputNumber size="small" value={singlePoint.precipitationMm} min={0} step={0.5}
                    onChange={v => v != null && handleSingleEdit(doy, 'precipitationMm', v)} style={{ width: 100 }} />
                  <Text type="secondary">мм</Text>
                </Space>
                <Space>
                  <Text style={{ width: 90 }}>Влажность:</Text>
                  <InputNumber size="small" value={singlePoint.humidityPct} min={0} max={100}
                    onChange={v => v != null && handleSingleEdit(doy, 'humidityPct', v)} style={{ width: 100 }} />
                  <Text type="secondary">%</Text>
                </Space>
                <Space>
                  <Text style={{ width: 90 }}>Часы осадков:</Text>
                  <InputNumber disabled={readOnly} size="small" min={0} max={24} step={0.5}
                    value={singlePoint.precipitationHours} placeholder="Авто"
                    onChange={v => handleSingleEdit(doy, 'precipitationHours', v ?? null)} style={{ width: 100 }} />
                </Space>
                <Space>
                  <Text style={{ width: 90 }}>Гроза:</Text>
                  <Select disabled={readOnly} size="small" style={{ width: 230 }}
                    value={singlePoint.thunderstormCode ?? -1}
                    onChange={v => handleSingleEdit(doy, 'thunderstormCode', v === -1 ? null : v)}
                    options={[
                      { value: -1, label: `По данным API (${singlePoint.weatherCode ?? 'нет кода'})` },
                      { value: 0, label: 'Без грозы' },
                      { value: 95, label: 'Гроза — WMO 95' },
                      { value: 96, label: 'Гроза, слабый град — WMO 96' },
                      { value: 99, label: 'Гроза, сильный град — WMO 99' },
                    ]} />
                </Space>
                <Space>
                  <Text style={{ width: 90 }}>Облачность:</Text>
                  <InputNumber size="small" value={+(singlePoint.cloudCover * 100).toFixed(0)} min={0} max={100}
                    onChange={v => v != null && handleSingleEdit(doy, 'cloudCover', v / 100)} style={{ width: 100 }} />
                  <Text type="secondary">%</Text>
                </Space>
              </Space>
            </>
          );
        })()}
      </Drawer>

      <Drawer
        open={showOverlay && isMulti}
        onClose={() => setSelectedCells(new Set())}
        title={`Редактирование ${selectedArray.length} дней`}
        width={380}
        mask={false}
        destroyOnClose
      >
        <Segmented
          block
          value={editMode}
          onChange={v => setEditMode(v as EditMode)}
          options={[
            { label: 'Относительно', value: 'relative' },
            { label: 'Точно', value: 'exact' },
          ]}
          style={{ marginBottom: 16 }}
        />

        {editMode === 'relative' ? (
          <Space direction="vertical" size={14} style={{ width: '100%' }}>
            <div>
              <Text style={{ fontSize: 12 }}>T ночь ± {adjustValues.tempMinBiasC > 0 ? '+' : ''}{adjustValues.tempMinBiasC}°C</Text>
              <Slider min={-20} max={20} step={1} value={adjustValues.tempMinBiasC}
                onChange={v => setAdjustValues(p => ({ ...p, tempMinBiasC: v }))}
                marks={{ '-20': '-20', 0: '0', 20: '+20' }} />
            </div>
            <div>
              <Text style={{ fontSize: 12 }}>T день ± {adjustValues.tempMaxBiasC > 0 ? '+' : ''}{adjustValues.tempMaxBiasC}°C</Text>
              <Slider min={-20} max={20} step={1} value={adjustValues.tempMaxBiasC}
                onChange={v => setAdjustValues(p => ({ ...p, tempMaxBiasC: v }))}
                marks={{ '-20': '-20', 0: '0', 20: '+20' }} />
            </div>
            <div>
              <Text style={{ fontSize: 12 }}>Осадки × {adjustValues.precipMul.toFixed(1)}</Text>
              <Slider min={0} max={3} step={0.1} value={adjustValues.precipMul}
                onChange={v => setAdjustValues(p => ({ ...p, precipMul: v }))}
                marks={{ 0: '0', 1: '1', 2: '2', 3: '3' }} />
            </div>
            <div>
              <Text style={{ fontSize: 12 }}>Облачность ± {adjustValues.cloudBias > 0 ? '+' : ''}{adjustValues.cloudBias}%</Text>
              <Slider min={-50} max={50} step={5} value={adjustValues.cloudBias}
                onChange={v => setAdjustValues(p => ({ ...p, cloudBias: v }))}
                marks={{ '-50': '-50', 0: '0', 50: '+50' }} />
            </div>
            <div css={styles.applyBtn} onClick={handleApplyRelative}>
              Применить к {selectedArray.length} дням
            </div>
          </Space>
        ) : (
          <Space direction="vertical" size={8} style={{ width: '100%' }}>
            <Space>
              <Text style={{ width: 90 }}>T ночь:</Text>
              <InputNumber size="small" value={exactValues.tempMin} step={0.5} placeholder="—"
                onChange={v => setExactValues(p => ({ ...p, tempMin: v ?? null }))} style={{ width: 100 }} />
              <Text type="secondary">°C</Text>
            </Space>
            <Space>
              <Text style={{ width: 90 }}>T день:</Text>
              <InputNumber size="small" value={exactValues.tempMax} step={0.5} placeholder="—"
                onChange={v => setExactValues(p => ({ ...p, tempMax: v ?? null }))} style={{ width: 100 }} />
              <Text type="secondary">°C</Text>
            </Space>
            <Space>
              <Text style={{ width: 90 }}>Осадки:</Text>
              <InputNumber size="small" value={exactValues.precipitationMm} min={0} step={0.5} placeholder="—"
                onChange={v => setExactValues(p => ({ ...p, precipitationMm: v ?? null }))} style={{ width: 100 }} />
              <Text type="secondary">мм</Text>
            </Space>
            <Space>
              <Text style={{ width: 90 }}>Влажность:</Text>
              <InputNumber size="small" value={exactValues.humidityPct} min={0} max={100} placeholder="—"
                onChange={v => setExactValues(p => ({ ...p, humidityPct: v ?? null }))} style={{ width: 100 }} />
              <Text type="secondary">%</Text>
            </Space>
            <Space>
              <Text style={{ width: 90 }}>Облачность:</Text>
              <InputNumber size="small"
                value={exactValues.cloudCover != null ? Math.round(exactValues.cloudCover * 100) : null}
                min={0} max={100} placeholder="—"
                onChange={v => setExactValues(p => ({ ...p, cloudCover: v != null ? v / 100 : null }))}
                style={{ width: 100 }} />
              <Text type="secondary">%</Text>
            </Space>
            <div css={styles.applyBtn} onClick={handleApplyExact}>
              Установить для {selectedArray.length} дней
            </div>
          </Space>
        )}
      </Drawer>
    </div>
  );
};

export default WeatherCalendarEditor;
